import { useCallback, useRef } from 'react'
import { useToast } from '../context/ToastContext'

// Toast-with-undo pattern for BULK mutations.
//
// Differs from usePendingDelete in one important way: the mutation fires
// IMMEDIATELY.  Bulk operations (close 20 matters, reassign 40 leads) shouldn't
// hold the UI in a stale-optimistic state for 5 seconds — the user just clicked
// Apply on a modal and expects the server to reflect their change.  Instead we
// capture a snapshot BEFORE apply runs, and if the user clicks Undo within the
// toast window, we call a caller-supplied revert() using that snapshot to
// reverse the change.
//
// The snapshot MUST be captured from local React state (not re-fetched from the
// server) so the pre-mutation values are trapped before apply modifies them.
//
// Usage (bulk reschedule on the Tasks page):
//   const undo = useBulkUndo()
//   const handleBulkReschedule = async (ids, delta_days) => {
//     undo.run({
//       ids,
//       snapshot: (ids) => ({ ids, delta_days }),
//       apply: () => api.put('/api/calendar/bulk/reschedule', { ids, delta_days }),
//       revert: (snap) => api.put('/api/calendar/bulk/reschedule', {
//         ids: snap.ids, delta_days: -snap.delta_days
//       }),
//       label: `Moved ${ids.length} items`,
//       onAfter: () => fetchEvents(),
//     })
//   }
//
// Matter status is different: a bulk close needs a closing note and only the
// server can put back what it changed (status, lock and close fields), so
// Matters.js reverts with the server's undo_token (PUT /api/matters/bulk/status
// with { undo_token }), never with per-matter PUTs from a local snapshot.

export function useBulkUndo() {
  const toast = useToast()
  // Track whether a given run has already been reverted or auto-dismissed so
  // a stale toast action can't fire twice.
  const active = useRef({})

  const run = useCallback(async ({
    ids,
    snapshot,
    apply,
    revert,
    label,
    delay = 6000,
    onAfter,
  }) => {
    // Capture snapshot FIRST — before apply mutates any local state via onAfter
    // or via optimistic updates the caller may weave in.
    let snap
    try {
      snap = snapshot ? snapshot(ids) : null
    } catch (err) {
      console.error('useBulkUndo: snapshot() threw', err)
      toast.error('Bulk action failed — could not capture undo state.')
      return
    }

    // Fire the mutation immediately.
    try {
      await apply()
    } catch (err) {
      const msg = (err && err.response?.data?.error) || (err && err.message) || 'Bulk action failed'
      toast.error(msg)
      return
    }

    try { onAfter?.() } catch (e) { console.error(e) }

    // Register this run so Undo click can find it.
    const key = `${Date.now()}::${Math.random().toString(36).slice(2, 6)}`
    active.current[key] = { done: false }

    // Label may be a function so the caller can compute it AFTER apply()
    // has returned (e.g. "Moved N of M …" using server-reported counts).
    let resolvedLabel
    try {
      resolvedLabel = typeof label === 'function' ? label() : label
    } catch (e) {
      console.error('useBulkUndo: label() threw', e)
      resolvedLabel = null
    }

    toast.custom({
      type: 'custom',
      message: resolvedLabel || 'Done',
      duration: delay,
      actions: [{
        label: 'Undo',
        onClick: async () => {
          const entry = active.current[key]
          if (!entry || entry.done) return
          entry.done = true
          try {
            if (revert) await revert(snap)
            try { onAfter?.() } catch (e) { console.error(e) }
            toast.success('Undone')
          } catch (err) {
            const msg = (err && err.response?.data?.error) || (err && err.message) || 'Undo failed'
            console.error('useBulkUndo: revert() threw', err)
            toast.error(`Undo failed — ${msg}`)
          } finally {
            delete active.current[key]
          }
        },
      }],
    })

    // Auto-cleanup registry entry after the toast window expires so long-lived
    // pages don't accumulate stale keys.
    setTimeout(() => {
      const entry = active.current[key]
      if (entry && !entry.done) {
        delete active.current[key]
      }
    }, delay + 500)
  }, [toast])

  return { run }
}
